import {
  MarketBrief,
  MarketHistory,
  MarketHistoryDailyMap,
  MarketHistorySampleSpan,
  MarketHistoryTimespan,
} from 'models/market'
import { promiseWrapper } from 'utils/promise'
import { getPeggedCurrencyPairs, PeggedPairConfig } from 'services/wp-api/pegged-currency-pairs-api'
import {
  isForexMarketBrief,
  onlyForexMarketBrief,
  onlyForexMarketHistory,
  peggedMarketBrief,
  peggedMarketHistoryDailyMap,
} from 'services/market-pegged'

declare global {
  interface Window {
    marketBrief: MarketBrief[]
    pairMarketBrief: MarketBrief
  }
}

declare let USER_BASE_URL_IK: string

// Market history

const fetchHistoryPublicApi = (
  pair: string,
  timeSpan: MarketHistoryTimespan,
  sampleSpan: MarketHistorySampleSpan,
): Promise<MarketHistory> =>
  promiseWrapper<MarketHistory, null>(
    `${USER_BASE_URL_IK}/api/public/marketHistory/${pair}/${timeSpan}/${sampleSpan}`,
    'GET',
  )

const fetchDailyHistoryPublicApi = (pair: string): Promise<MarketHistoryDailyMap> =>
  promiseWrapper<MarketHistoryDailyMap, null>(
    `${USER_BASE_URL_IK}/api/public/marketHistory/dailyFxData?pairs[]=${pair}`,
    'GET',
  )

const isForexMarketHistoryData = (history: MarketHistory): boolean => Boolean(history?.fx_series?.length)

const fetchSyntheticHistory = (
  pair: string,
  timeSpan: MarketHistoryTimespan,
  sampleSpan: MarketHistorySampleSpan,
  pegged: PeggedPairConfig,
): Promise<MarketHistory> =>
  fetchHistoryPublicApi(pegged.sourcePair, timeSpan, sampleSpan).then((source) =>
    onlyForexMarketHistory(source, pair, pegged.pegRate),
  )

const fetchMarketHistory = async (
  pair: string,
  timeSpan: MarketHistoryTimespan,
  sampleSpan: MarketHistorySampleSpan,
): Promise<MarketHistory> => {
  const peggedPairs = await getPeggedCurrencyPairs()
  const pegged = peggedPairs[pair]
  if (!pegged) return fetchHistoryPublicApi(pair, timeSpan, sampleSpan)

  return fetchHistoryPublicApi(pair, timeSpan, sampleSpan)
    .then((data) => (isForexMarketHistoryData(data) ? data : fetchSyntheticHistory(pair, timeSpan, sampleSpan, pegged)))
    .catch(() => fetchSyntheticHistory(pair, timeSpan, sampleSpan, pegged))
}

const fetchMarketHistoryDaily = async (pair: string): Promise<MarketHistoryDailyMap> => {
  const [data, peggedPairs] = await Promise.all([fetchDailyHistoryPublicApi(pair), getPeggedCurrencyPairs()])
  return peggedMarketHistoryDailyMap(data, peggedPairs)
}

// Market brief

const fetchBriefPublicApi = (pair: string): Promise<MarketBrief> =>
  promiseWrapper<MarketBrief, null>(`${USER_BASE_URL_IK}/api/public/marketBrief/${pair}`, 'GET')

const fetchAllMarketBriefs = (): Promise<MarketBrief[]> =>
  promiseWrapper<MarketBrief[], null>(`${USER_BASE_URL_IK}/api/public/marketBrief`, 'GET')

const fetchSyntheticBrief = (currency_pair: string, pegged: PeggedPairConfig): Promise<MarketBrief> =>
  fetchBriefPublicApi(pegged.sourcePair).then((source) => onlyForexMarketBrief(source, currency_pair, pegged.pegRate))

const fetchMarketBriefPair = async (currency_pair: string): Promise<void> => {
  const peggedPairs = await getPeggedCurrencyPairs()
  const pegged = peggedPairs[currency_pair]

  const request = !pegged
    ? fetchBriefPublicApi(currency_pair)
    : fetchBriefPublicApi(currency_pair)
        .then((data) => (isForexMarketBrief(data) ? data : fetchSyntheticBrief(currency_pair, pegged)))
        .catch(() => fetchSyntheticBrief(currency_pair, pegged))

  return request.then(
    (data) => {
      window.pairMarketBrief = data
      window.dispatchEvent(new Event('pairMarketBrief fetched'))
    },
    () => {
      window.dispatchEvent(new Event('pairMarketBrief fetch fail'))
    },
  )
}

const fetchMarketBrief = async (): Promise<void> => {
  try {
    const [data, peggedPairs] = await Promise.all([fetchAllMarketBriefs(), getPeggedCurrencyPairs()])
    window.marketBrief = peggedMarketBrief(data, peggedPairs)
    window.dispatchEvent(new Event('marketBrief fetched'))
  } catch {
    window.dispatchEvent(new Event('marketBrief fetch fail'))
  }
}

const getMarketBrief = (): MarketBrief[] => window.marketBrief || []

const marketService = {
  fetchMarketHistory,
  fetchMarketHistoryDaily,
  fetchMarketBriefPair,
  fetchMarketBrief,
  getMarketBrief,
}

export default marketService
